import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AuthState,
  fetchSession,
  getAuthState,
  login,
  logout,
  reauthSilently,
  shouldAttemptFactSetAutoLogin,
} from "../auth/authService";
import { AUTH_EXPIRED_EVENT } from "../utils/authStatus";
import { getAuthHostServices } from "../auth/authHostServices";
import { useAnalyticsAuth } from "./useAnalyticsAuth";
import { logBrandingFlagDiag, maskSdkKey } from "../featureFlags/brandingFlagDiag";
import { setSkillsCatalogUserScope } from "../skills/catalogStore";
import { setUserFilesUserScope } from "../api/finster/userFilesStore";
import { buildAccountScopeKey } from "../auth/accountScope";

interface UseAuthProps {
  log: (message: string) => void;
}

export function buildAuthAccountScopeKey(authState: AuthState): string | null {
  if (!authState.isAuthenticated) return null;
  return buildAccountScopeKey({
    userId: authState.user?.id,
    userEmail: authState.user?.email,
    companyId: authState.user?.companyId,
  });
}

export const useAuth = ({ log }: UseAuthProps) => {
  const [authState, setAuthState] = useState<AuthState>(() => getAuthState());
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const hostServices = getAuthHostServices();
  const accountScopeKey = buildAuthAccountScopeKey(authState);

  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  // Check auth state on mount
  useEffect(() => {
    let cancelled = false;
    const pathname = window.location.pathname.toLowerCase();
    const isPasswordResetPage =
      pathname.includes("/login/forgot-password") || pathname.includes("/login/reset-password");

    const hostInfo = (window as unknown as { __FINSTER_HOST_INFO__?: Record<string, unknown> })
      .__FINSTER_HOST_INFO__;
    if (hostInfo) {
      logRef.current(`🔍 Host Detection: host="${hostInfo.host}", isExcel=${hostInfo.isExcel}`);
      logRef.current(
        `   typeof Excel="${hostInfo.typeofExcel}", typeof PowerPoint="${hostInfo.typeofPowerPoint}"`
      );
    }

    const restoreSession = async () => {
      try {
        const state = await fetchSession();
        if (cancelled) return;

        if (state.isAuthenticated || isPasswordResetPage || !shouldAttemptFactSetAutoLogin()) {
          setAuthState(state);
          return;
        }

        logRef.current("No active session; trying FactSet SSO login...");
        try {
          const ssoState = await login();
          if (cancelled) return;

          if (ssoState.isAuthenticated) {
            logRef.current(`FactSet SSO login successful: ${ssoState.user?.email}`);
          } else {
            logRef.current("FactSet SSO login did not authenticate");
          }
          setAuthState(ssoState);
        } catch (error) {
          if (cancelled) return;

          const errorMsg = error instanceof Error ? error.message : String(error);
          logRef.current(`FactSet SSO auto-login unavailable: ${errorMsg}`);
          setAuthState(state);
        }
      } catch (error) {
        console.error("Failed to restore session:", error);
        if (!cancelled) {
          setAuthState(getAuthState());
        }
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Reauth coordination lives in `services/authService.ts` so that
    // `fetchWithRetry` and this hook share a single in-flight promise
    // and a single loop guard — i.e. a 401 burst opens at most one
    // Office dialog and downstream React state stays consistent with
    // whichever branch (silent retry / fall through to login) the
    // fetch wrapper ended on.
    //
    // This listener exists for the case where reauth was refused
    // (AuthKit build, loop guard active, dialog dismissed) — we need
    // to flip React state to <Login>. On the success path
    // `fetchWithRetry` swallows the 401 entirely and never fires
    // AUTH_EXPIRED, so no React update is needed and the user keeps
    // their place in the chat.
    const handleAuthExpired = () => {
      void (async () => {
        // `reauthSilently()` no-ops if a previous attempt already ran
        // recently (loop guard) or is still in flight (single-flight),
        // so this call is cheap when fetchWithRetry has already
        // tried and failed — we just observe its result here.
        const state = await reauthSilently();
        if (state?.isAuthenticated) {
          logRef.current(`Silent reauth successful: ${state.user?.email}`);
          setAuthState(state);
          return;
        }
        logRef.current("Session expired. Please sign in again.");
        setAuthState(getAuthState());
        setAuthChecked(true);
      })();
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, []);

  useLayoutEffect(() => {
    setSkillsCatalogUserScope(accountScopeKey);
    setUserFilesUserScope(accountScopeKey);
    hostServices.setUserScope?.(accountScopeKey);
  }, [accountScopeKey, hostServices]);

  useLayoutEffect(() => {
    if (authState.isAuthenticated && authState.user?.email) {
      logBrandingFlagDiag("authInitFeatureFlags", {
        userEmail: authState.user.email,
        companyId: authState.user.companyId || null,
        sessionConfigCatSdkKey: authState.features?.configcatSdkKey
          ? maskSdkKey(authState.features.configcatSdkKey)
          : null,
      });
      Promise.resolve(
        hostServices.initFeatureFlags?.(authState.user.email, {
          sdkKey: authState.features?.configcatSdkKey,
          accountScopeKey,
          companyId: authState.user.companyId,
        })
      ).catch((error) => {
        console.warn("Failed to initialize feature flags:", error);
        logBrandingFlagDiag("authInitFeatureFlagsFailed", {
          userEmail: authState.user?.email,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    } else {
      logBrandingFlagDiag("authDisposeFeatureFlags", {
        isAuthenticated: authState.isAuthenticated,
      });
      hostServices.disposeFeatureFlags?.();
    }
  }, [
    accountScopeKey,
    authState.isAuthenticated,
    authState.user?.email,
    authState.user?.companyId,
    authState.features?.configcatSdkKey,
    hostServices,
  ]);

  useAnalyticsAuth(authState);

  // Auto-connect FactSet OAuth right after WorkOS authentication so the
  // TTF gate (`excel/excelService.ts::computeTtfRunContext`) flips to
  // open without any explicit user action. Without this, the per-user
  // FactSet access token never lands in the session and every TTF call
  // silently falls back to the supplied formula — which was the
  // root cause of "TTF didn't help" reports despite the API being fully
  // wired end-to-end.
  //
  // Gated on `shouldAttemptFactSetAutoLogin()` — the same build-time
  // flag (`__FACTSET_AUTO_SSO_ENABLED__`) that opts the FactSet ribbon
  // build into auto-SSO. The non-FactSet (AuthKit) build has no
  // FactSet OAuth endpoint to connect to, so we skip the probe entirely.
  //
  // `ensureFactSetOAuthConnected()` is single-flight, idempotent, and
  // never throws — it returns a typed outcome we log to the activity
  // log so the user / support can see whether the connect happened.
  // A failed/dismissed attempt is remembered in sessionStorage so we
  // don't re-prompt within the same session.
  useEffect(() => {
    if (!authState.isAuthenticated) return undefined;
    if (!shouldAttemptFactSetAutoLogin()) return undefined;

    let cancelled = false;
    void (async () => {
      try {
        const result = await hostServices.ensureFactSetOAuthConnected?.();
        if (!result) return;
        if (cancelled) return;
        switch (result.outcome) {
          case "already_connected":
            logRef.current("FactSet connection already active.");
            break;
          case "connected":
            logRef.current("Connected to FactSet for Text-to-Formula.");
            break;
          case "dismissed":
            logRef.current(
              "FactSet connection skipped — Text-to-Formula formula generation may be unavailable."
            );
            break;
          case "unavailable":
            logRef.current(
              `FactSet connection probe unavailable${
                result.error ? `: ${result.error}` : ""
              }`
            );
            break;
          case "failed":
            logRef.current(
              `FactSet connection failed${
                result.error ? `: ${result.error}` : ""
              } — Text-to-Formula formula generation may be unavailable.`
            );
            break;
        }
      } catch (error) {
        // ensureFactSetOAuthConnected is documented as never-throws, but
        // belt-and-suspenders: a defect here must not crash the addin.
        if (cancelled) return;
        console.warn("FactSet auto-connect threw unexpectedly:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated, hostServices]);

  // Handle login
  const handleLogin = async () => {
    try {
      log("Starting WorkOS login...");
      const state = await login(shouldAttemptFactSetAutoLogin() ? { method: "factset" } : {});
      if (state.isAuthenticated) {
        log(`Login successful: ${state.user?.email}`);
      } else {
        log("Login cancelled or failed");
      }
      setAuthState(state);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      log(`Login error: ${errorMsg}`);
      throw e; // Re-throw so Login component shows the error
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    setAuthState(getAuthState());
  };

  // `accountScopeKey` is tenant-aware where a bare user id is not: the same
  // user switching company must invalidate anything scoped to the old account.
  return { authState, authChecked, accountScopeKey, handleLogin, handleLogout };
};
